import { useEffect } from 'react';
import { SendMessage } from 'react-use-websocket';
import {
  Outlet,
  useNavigate,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { PermissionEnum, PermissionDenied, IconLoader } from '@ui/src';
import { useLazyGetEmployeesDictionariesQuery } from '@administration/store/dictionaries/service';
import { useAppSelector } from '@store';
import {
  useLazyGetCustomersDictionariesQuery,
  useLazyGetCustomerEmployeeQuery,
  useLazyGetClientsBaseCurrencyQuery,
  useLazyGetCustomersCountriesQuery,
  useLazyGetCustomersCurrenciesQuery,
  useLazyGetCustomersSettingsQuery,
} from '../../store/dictionaries/services';

import ROUTES from '../../routes';

import cn from './customers.module.css';

const Customers = (): React.JSX.Element => {
  const navigate = useNavigate();

  const { sendMessage } = useOutletContext<{
    connectionStatus: string | null;
    sendMessage: SendMessage | null;
  }>();

  const {
    activePermissions,
    currentEmployee,
    countries,
    currencies,
    beneficiarySettings,
    baseCurrency,
  } = useAppSelector((state) => state.customersDictionaries);
  const { users } = useAppSelector((state) => state.administrationDictionaries);

  const match = useMatch(ROUTES.CUSTOMERS);

  const [getDictionaries] = useLazyGetCustomersDictionariesQuery();
  const [getCustomersCountries] = useLazyGetCustomersCountriesQuery();
  const [getCustomersCurrencies] = useLazyGetCustomersCurrenciesQuery();
  const [getCustomersSettings] = useLazyGetCustomersSettingsQuery();
  const [getCustomersBaseCurrency] = useLazyGetClientsBaseCurrencyQuery();
  const [getEmployee] = useLazyGetCustomerEmployeeQuery();
  const [getEmployeeDictionaries] = useLazyGetEmployeesDictionariesQuery();

  useEffect(() => {
    if (
      !users &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.USERS_LIST)
    ) {
      getEmployeeDictionaries();
    }
  }, [users, activePermissions]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS)) {
      getDictionaries();

      if (!currentEmployee) {
        getEmployee();
      }

      if (!countries) {
        getCustomersCountries();
      }

      if (!currencies) {
        getCustomersCurrencies();
      }

      if (!baseCurrency) {
        getCustomersBaseCurrency();
      }

      if (!beneficiarySettings) {
        getCustomersSettings();
      }

      if (match) {
        navigate(ROUTES.CUSTOMERS_INDIVIDUAL);
      }
    }
  }, [
    match,
    activePermissions,
    currentEmployee,
    currencies,
    countries,
    baseCurrency,
    beneficiarySettings,
  ]);

  let content = (
    <div className={cn.customersLoader}>
      <IconLoader />
    </div>
  );

  useEffect(() => {
    let title = 'Clients';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  if (
    activePermissions &&
    !activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS)
  ) {
    content = <PermissionDenied />;
  } else if (
    activePermissions &&
    activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS)
  ) {
    content = <Outlet context={{ sendMessage }} />;
  }

  return content;
};

const CustomersWrapper = () => <Customers />;

export default CustomersWrapper;
